import * as React from 'react';
import {
    BooleanInput,
    useDataProvider,
    useInput,
    useNotify,
} from 'react-admin';
import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Typography,
} from '@material-ui/core';

const normalizeId = (value: any) => {
    if (value === undefined || value === null)
        return '';

    if (typeof value === 'string')
        return value;

    return String(value.id || value._id || value);
};

export const DemoStatusCheckbox = ({
    demoMode,
}: {
    demoMode: boolean;
}) => (
    <FormControlLabel
        control={
            <Checkbox
                checked={Boolean(demoMode)}
                disabled
            />
        }
        label="Démo"
    />
);

const OtherCatalogsInput = () => {
    const dataProvider: any = useDataProvider();
    const notify = useNotify();

    const { input } = useInput({
        source: 'catalog_ids',
    });

    const values = Array.isArray(input.value)
        ? input.value
              .map(normalizeId)
              .filter(Boolean)
        : [];

    const [catalogs, setCatalogs] =
        React.useState<any[]>([]);

    const [enabled, setEnabled] =
        React.useState(values.length > 0);

    React.useEffect(() => {
        let mounted = true;

        dataProvider
            .getList('catalog', {
                pagination: {
                    page: 1,
                    perPage: 1000,
                },
                sort: {
                    field: 'name',
                    order: 'ASC',
                },
                filter: {},
            })
            .then((response: any) => {
                if (!mounted)
                    return;

                setCatalogs(
                    (response.data || []).filter(
                        (catalog: any) =>
                            catalog.active !== false &&
                            catalog.slug !== 'general'
                    )
                );
            })
            .catch(() => {
                if (mounted)
                    notify(
                        'Impossible de charger les autres catalogues',
                        'warning'
                    );
            });

        return () => {
            mounted = false;
        };
    }, [dataProvider, notify]);

    React.useEffect(() => {
        if (values.length > 0)
            setEnabled(true);
    }, [values.length]);

    const namesById = new Map(
        catalogs.map((catalog: any) => [
            String(catalog.id),
            catalog.name,
        ])
    );

    return (
        <Box mb={2}>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={enabled}
                        onChange={(event) => {
                            const checked =
                                event.target.checked;

                            setEnabled(checked);

                            if (!checked)
                                input.onChange([]);
                        }}
                    />
                }
                label="Autre catalogue"
            />

            {enabled && (
                <Box mt={1} ml={4}>
                    {catalogs.length ? (
                        <FormControl
                            variant="outlined"
                            size="small"
                            style={{
                                minWidth: 320,
                                maxWidth: 600,
                            }}
                        >
                            <InputLabel>
                                Catalogues
                            </InputLabel>

                            <Select
                                multiple
                                value={values}
                                onChange={(event) =>
                                    input.onChange(
                                        event.target.value
                                    )
                                }
                                label="Catalogues"
                                renderValue={(
                                    selected: any
                                ) =>
                                    (selected || [])
                                        .map(
                                            (id: string) =>
                                                namesById.get(
                                                    String(id)
                                                ) ||
                                                String(id)
                                        )
                                        .join(', ')
                                }
                            >
                                {catalogs.map(
                                    (catalog: any) => (
                                        <MenuItem
                                            key={catalog.id}
                                            value={String(
                                                catalog.id
                                            )}
                                        >
                                            <Checkbox
                                                checked={values.includes(
                                                    String(
                                                        catalog.id
                                                    )
                                                )}
                                            />

                                            {catalog.name}
                                        </MenuItem>
                                    )
                                )}
                            </Select>
                        </FormControl>
                    ) : (
                        <Typography
                            variant="body2"
                            color="textSecondary"
                        >
                            Aucun autre catalogue disponible.
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
};

export const ContentCatalogMembershipInputs = ({
    defaultStandard = true,
}: {
    defaultStandard?: boolean;
}) => (
    <>
        <BooleanInput
            source="is_standard"
            label="Standard"
            defaultValue={defaultStandard}
        />

        <OtherCatalogsInput />
    </>
);
